import { ActionItem, ActionItemCategory, FeedEvent } from '@urbanmix-tech/shared-js';
import { getFeedEvents } from '@/data';
import { useAuth } from '@/shared/context/AuthProvider';
import { twMerge } from 'tailwind-merge';
import { convertPseudoDateToRealDate } from '@/helpers/common';
import { useMemo, useState, useEffect } from 'react';
import { Button, IconButton, Badge, Stack, Typography, Tooltip } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import WarningIcon from '@mui/icons-material/Warning';
import CommentIcon from '@mui/icons-material/Comment';
import PhotoIcon from '@mui/icons-material/Photo';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FlagIcon from '@mui/icons-material/Flag';
import CategoryIcon from '@mui/icons-material/Category';
import { TaskEditDialog } from './TaskEditDialog';
import { useSignedUrls } from '@/shared/hooks/useSignedUrls';

// Status colors by category
const statusColorByCategory: Record<ActionItemCategory | string, string> = {
  inspect: 'bg-rose-500',
  makeReady: 'bg-orange-400',
  notice: 'bg-purple-400',
  lease: 'bg-emerald-400',
  general: 'bg-gray-400',
};

// Priority colors for flag icon
const priorityColors: Record<string, string> = {
  low: '#4ade80',    // green-400
  medium: '#fbbf24', // amber-400
  high: '#f87171',   // red-400
};

export type TaskListProps = {
  actionItems: ActionItem[];
  showFutureTasks?: boolean;
  showIgnoredTasks?: boolean;
  onClickActionItem: (actionItem: ActionItem) => void;
  onHoverItem?: (actionItem?: ActionItem) => void;
  editMode?: boolean;
};

export function TaskList({
  actionItems,
  onClickActionItem,
  onHoverItem,
  showFutureTasks = true,
  showIgnoredTasks = false,
  editMode,
}: TaskListProps) {
  const authContext = useAuth();
  const { usersData } = authContext;
  const currentUserId = authContext.auth.currentUser?.uid;
  const [hoveredActionItem, setHoveredActionItem] = useState<ActionItem | null>(
    null
  );
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedActionItem, setSelectedActionItem] = useState<ActionItem | null>(
    null
  );
  const [commentCounts, setCommentCounts] = useState<Record<string, number>>({});

  // Collect first image from each inspect task for signed URL conversion
  const allInspectImages = useMemo(() => {
    return actionItems
      .filter((item) => ['inspect', 'track', 'work', 'review'].includes(item.category))
      .map((item) => (item.response?.images || [])[0])
      .filter(Boolean);
  }, [actionItems]);

  // Fetch thumbnail URLs with auto-fallback to full-size (handled by hook)
  const signedUrls = useSignedUrls(allInspectImages, { useThumbnails: true });

  // Fetch comment counts for all action items
  useEffect(() => {
    const fetchCommentCounts = async () => {
      const counts: Record<string, number> = {};

      // Get unique units to minimize API calls
      const uniqueUnits = Array.from(
        new Set(actionItems.map((item) => item.unitId))
      );

      try {
        for (const unitId of uniqueUnits) {
          const firstItem = actionItems.find((item) => item.unitId === unitId);
          if (!firstItem) continue;

          const events = await getFeedEvents({
            unitId: unitId,
            projectId: firstItem.projectId,
            buildingId: firstItem.buildingId,
          });

          // Filter to task_comment events and count comments per task
          events
            .filter((event: FeedEvent) => event.eventType === 'task_comment')
            .forEach((event: FeedEvent) => {
              const taskId = event.eventData?.taskId;
              if (taskId) {
                counts[taskId] = (counts[taskId] || 0) + 1;
              }
            });
        }
        setCommentCounts(counts);
      } catch (error) {
        console.error('Error fetching comment counts:', error);
      }
    };

    if (actionItems.length > 0) {
      fetchCommentCounts();
    }
  }, [actionItems]);

  const handleMouseEnter = (actionItem: ActionItem) => {
    onHoverItem && onHoverItem(actionItem);
    setHoveredActionItem(actionItem);
  };

  const handleMouseLeave = () => {
    onHoverItem && onHoverItem();
    setHoveredActionItem(null);
  };

  const handleEditClick = (e: React.MouseEvent, actionItem: ActionItem) => {
    e.stopPropagation();
    setSelectedActionItem(actionItem);
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setSelectedActionItem(null);
  };

  const getStatusColor = (item: ActionItem) => {
    if (item.status === 'active' && item.category === 'inspect') {
      return 'bg-rose-500';
    }
    return statusColorByCategory[item.category] || 'bg-gray-500';
  };

  const taskElements = actionItems
    .filter((actionItem) => {
      if (actionItem.status === 'ignored' && !showIgnoredTasks) return false;
      if (actionItem.status === 'future' && !showFutureTasks) return false;
      return true;
    })
    .sort((a, b) => {
      // Sort by due date, earlier tasks first
      const aDate = convertPseudoDateToRealDate(a.dueDate)?.getTime() ?? Infinity;
      const bDate = convertPseudoDateToRealDate(b.dueDate)?.getTime() ?? Infinity;
      return aDate - bDate;
    })
    .map((actionItem, index, filteredItems) => {
      const user =
        actionItem.owner?.userId && usersData
          ? usersData[actionItem.owner?.userId]
          : null;

      const isFutureTask = actionItem.status === 'future';
      const dueDate = convertPseudoDateToRealDate(actionItem.dueDate);
      const isOverdue =
        dueDate &&
        actionItem.status !== 'done' &&
        actionItem.status !== 'future' &&
        dueDate < new Date(new Date().setHours(0, 0, 0, 0));

      const taskImage = ['inspect', 'makeReady', 'track', 'work', 'review'].includes(actionItem.category) ? actionItem.response?.images?.[0] : null;
      const thumbnailSize = { width: '50px', height: '50px' }

      return (
        <div key={actionItem.id} className="border-b border-white/5 last:border-b-0">
          <div
            className={twMerge(
              'group flex cursor-pointer flex-row items-center px-16 py-12 bg-zinc-900/40 hover:bg-white/5 transition-colors',
              isFutureTask && 'opacity-50'
            )}
            onClick={() => onClickActionItem(actionItem)}
            onMouseEnter={() => handleMouseEnter(actionItem)}
            onMouseLeave={handleMouseLeave}
          >
            {/* Status indicator bar */}
            {/* <div
              className={twMerge(
                'mr-6 h-[50px] w-2 rounded-full',
                getStatusColor(actionItem)
              )}
            /> */}

            {/* Single thumbnail for inspect / makeReady tasks */}
            {taskImage && (
              <div className="mr-8" style={thumbnailSize}>
                {signedUrls[taskImage.id] ? (
                  <img
                    src={signedUrls[taskImage.id]}
                    alt=""
                    className="rounded object-cover w-full h-full"
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      (e.currentTarget.nextElementSibling as HTMLElement)?.style.setProperty('display', 'block');
                      (signedUrls as any).onLoadError?.(taskImage.id);
                    }}
                    onLoad={(e) => {
                      e.currentTarget.style.display = 'block';
                      (e.currentTarget.nextElementSibling as HTMLElement)?.style.setProperty('display', 'none');
                    }}
                  />
                ) : null}
                <div
                  className="w-full h-full bg-zinc-800 rounded-lg animate-pulse"
                  style={{ display: signedUrls[taskImage.id] ? 'none' : 'block' }}
                />
              </div>
            )}

            {editMode && (
              <Button
                variant="text"
                color="primary"
                onClick={() => onClickActionItem(actionItem)}
              >
                +
              </Button>
            )}

            {/* Task title and date underneath */}
            <div
              className={twMerge(
                'flex-1 text-sm text-stone-200',
                actionItem.status === 'done' ? 'text-stone-500 line-through' : '',
                isFutureTask && 'italic text-stone-400'
              )}
            >
              <div className="flex items-center gap-2">
                <div>
                  <span className="font-medium">{actionItem.title}</span>
                  {dueDate && (
                    <Typography 
                      variant="caption" 
                      className={twMerge(
                        "block text-stone-500",
                        actionItem.status === 'done' ? "line-through opacity-60" : ""
                      )}
                    >
                      {dueDate.toLocaleDateString()}
                    </Typography>
                  )}
                </div>
              </div>
            </div>

            {/* Icons row with consistent spacing */}

            <Stack direction="row" alignItems="center" spacing={1}>
              {/* Comments icon */}
              {commentCounts[actionItem.id] > 0 && (
                  <Tooltip title="Comments available" arrow>
                    <Badge
                      badgeContent={commentCounts[actionItem.id]}
                      color="primary"
                      sx={{
                        '& .MuiBadge-badge': {
                          fontSize: '0.65rem',
                          height: '16px',
                          minWidth: '16px',
                          padding: '0 4px',
                        },
                      }}
                    >
                      <CommentIcon className="text-zinc-400" sx={{ fontSize: 18 }} />
                    </Badge>
                  </Tooltip>
                )}

              {/* Priority icon */}
              {actionItem.response?.priority && (
                <Tooltip title={`Priority: ${actionItem.response.priority}`} arrow>
                  <FlagIcon
                    sx={{
                      fontSize: 18,
                      color: priorityColors[actionItem.response.priority] || '#9ca3af',
                    }}
                  />
                </Tooltip>
              )}

              

              {/* Overdue warning */}
              {isOverdue && (
                <Tooltip title="Overdue" arrow>
                  <WarningIcon sx={{ fontSize: 18, color: 'warning.main' }} />
                </Tooltip>
              )}

              {/* Action required attention icon */}
              {/* {actionItem.status === 'active' && (
                <Tooltip title="Action Required" arrow>
                  <ErrorOutlineIcon className='h-20 text-gray-300' />
                </Tooltip>
              )} */}

              {/* Assignee and Edit button */}

              <div className='flex gap-4 mr-4 items-center'>
              {/* Assignee */}
              {user && (
                <Stack direction="row" alignItems="center" gap={1} className="justify-center px-8 h-[30px] bg-zinc-800/70 border border-white/10 rounded-lg">
                  <PersonOutlineIcon className='text-stone-200' sx={{ fontSize: 16 }} />
                  <Typography variant="caption" className="text-stone-200 font-medium text-xs">
                    {user.fullName?.split(' ')[0] || ''}
                  </Typography>
                </Stack>
              )}

              {/* Edit button - faded, visible on row hover */}
                {actionItem.status !== 'done' && (
                  <Tooltip title="Edit" arrow>
                    <IconButton
                      size="small"
                      onClick={(e) => handleEditClick(e, actionItem)}
                      className="bg-zinc-800/70 border border-white/10 text-stone-400 h-[30px] w-[30px] rounded-lg transition-colors hover:text-white hover:bg-white/10"
                      sx={{ padding: '4px' }}
                    >
                      <EditIcon sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                )}

            </div>

            </Stack>
          </div>

          {/* Divider */}
          {index !== filteredItems.length - 1 && (
            <hr className="border-t border-zinc-700" />
          )}
        </div>
      );
    });

  return (
    <>
      {taskElements}
      {selectedActionItem && (
        <TaskEditDialog
          actionItem={selectedActionItem}
          open={editDialogOpen}
          onClose={handleCloseEditDialog}
        />
      )}
    </>
  );
}
